import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, MapPin, ClipboardList, Users, Building2, ChevronDown, Network, ExternalLink, Globe, Trash2, PowerOff, Power } from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useCrud } from '../../api/hooks/useCrud';
import { api } from '../../api/client';
import { DataTable } from '../../components/ui/DataTable';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal, PasswordConfirmModal } from '../../components/ui';
import { UserManagerTab } from '../../components/gestao/UserManagerTab';
import { useDevStore } from '../../store/useDevStore';
import '../../components/ui/datatable.css';
import { ROUTES } from '../../api/routes';
import { useAuthStore } from '../../store/authStore';
import { hasPermission } from '../../auth/permissions';

interface Rede { id: string; nome: string; descricao?: string; }
interface Estado { id: string; nome: string; sigla: string; redeId?: string; ativo?: boolean; }
interface Municipio {
  id: string; nome: string; codigoIBGE?: string; estadoId?: string; redeId?: string; ativo: boolean;
  estado?: { id: string; nome: string; sigla: string; rede?: { id: string; nome: string } };
  rede?: { id: string; nome: string; descricao?: string };
  latitude?: number | null;
  longitude?: number | null;
  _count?: { escolas: number; usuarios: number; gestores?: number };
  criadoEm: string;
}
interface RelEscola { id: string; nome: string; codigo: string; tipoRede: string; cidade?: string; ativo: boolean; _count?: { alunos: number; turmas: number } }

// Custom Leaflet pins
const markerPinIcon = L.divIcon({
  className: '',
  html: `<div style="font-size: 30px; transform: translate(-15px, -30px); filter: drop-shadow(0 4px 6px rgba(0,0,0,0.4));">📍</div>`,
  iconSize: [30, 30],
  iconAnchor: [0, 0]
});

// Component to dynamically re-center map
function MapCenterController({ center }: { center: [number, number] }) {
  const map = useMap();
  const lat = center[0];
  const lng = center[1];
  useEffect(() => {
    if (isFinite(lat) && isFinite(lng)) {
      map.setView([lat, lng], 13);
    }
  }, [lat, lng, map]);
  return null;
}

// Component to handle map clicks
function MapClickEvents({ onClick }: { onClick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onClick(e.latlng.lat, e.latlng.lng);
    }
  });
  return null;
}

export function MunicipiosPage() {
  const { user } = useAuthStore();
  const isEstadual = user?.perfil === 'ESTADUAL';
  const userEstadoId = user?.estadoId;
  const canManage = isEstadual ? hasPermission(user, 'gestao.manage') : true;

  const devSettings = useDevStore((s) => s.settings);
  const loc = useLocation();
  const navigate = useNavigate();
  const { data, isLoading, create, update, deactivate, refetch } = useCrud<Municipio>('admin/municipios');
  const [redes, setRedes] = useState<Rede[]>([]);
  const [estados, setEstados] = useState<Estado[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Municipio | null>(null);
  const [editTab, setEditTab] = useState<'dados' | 'escolas' | 'gestores'>('dados');
  const [form, setForm] = useState({ nome: '', codigoIBGE: '', redeId: '', estadoId: '', latitude: '', longitude: '' });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Municipio | null>(null);
  const [municipioToHardDelete, setMunicipioToHardDelete] = useState<Municipio | null>(null);

  // Related escolas
  const [relEscolas, setRelEscolas] = useState<RelEscola[]>([]);
  const [relLoading, setRelLoading] = useState(false);

  // Todos estados carregados (adicionado para buscar redeId do estado selecionado)
  const [allEstados, setAllEstados] = useState<Estado[]>([]);
  const [allRedesGlobal, setAllRedesGlobal] = useState<Rede[]>([]);
  const [allEstadosGlobal, setAllEstadosGlobal] = useState<Estado[]>([]);
  const [allEscolasGlobal, setAllEscolasGlobal] = useState<any[]>([]);

  useEffect(() => {
    api.get(ROUTES.admin.redes).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
      setAllRedesGlobal(list);
      const activeRedes = list.filter((x: any) => x.ativo === true);
      setRedes(activeRedes);
    }).catch(() => {});

    api.get(ROUTES.admin.estados).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
      setAllEstadosGlobal(list);
      const activeEstados = list.filter((x: any) => x.ativo === true);
      setAllEstados(activeEstados);

      if (isEstadual && userEstadoId) {
        const matchingEstado = activeEstados.find((e: Estado) => e.id === userEstadoId);
        if (matchingEstado) {
          setEstados([matchingEstado]);
          const matchingRedeId = matchingEstado.redeId || user?.redeId || '';
          setForm(prev => ({
            ...prev,
            estadoId: userEstadoId,
            redeId: matchingRedeId,
          }));
        }
      }
    }).catch(() => {});

    api.get(ROUTES.escolas.listar).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
      setAllEscolasGlobal(list);
    }).catch(() => {});
  }, [isEstadual, userEstadoId, user?.redeId]);

  // Cascata: quando rede muda, buscar estados filtrados (apenas quando não for estadual com estado fixo)
  useEffect(() => {
    if (isEstadual && userEstadoId) return;
    if (!form.redeId) { setEstados([]); return; }
    api.get(ROUTES.admin.estadosPorRede(form.redeId)).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
      const activeEstados = list.filter((x: any) => x.ativo !== false);
      setEstados(activeEstados);
      setForm(prev => {
        if (!prev.estadoId && activeEstados.length === 1) {
          return { ...prev, estadoId: activeEstados[0].id };
        }
        return prev;
      });
    }).catch(() => setEstados([]));
  }, [form.redeId, isEstadual, userEstadoId]);

  // Fetch escolas when switching to tab
  useEffect(() => {
    if (!editing || editTab !== 'escolas' || relEscolas.length > 0) return;
    setRelLoading(true);
    api.get(ROUTES.escolas.listar).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
      setRelEscolas(list.filter((e: any) => e.municipioId === editing.id || e.municipio?.id === editing.id));
    }).catch(() => {}).finally(() => setRelLoading(false));
  }, [editing, editTab]);

  const openNew = () => {
    setEditing(null);
    let defaultRedeId = '';
    let defaultEstadoId = '';

    if (isEstadual && userEstadoId) {
      defaultEstadoId = userEstadoId;
      const matchingEstado = allEstados.find(e => e.id === userEstadoId);
      defaultRedeId = matchingEstado?.redeId || user?.redeId || '';
      if (matchingEstado) {
        setEstados([matchingEstado]);
      }
    } else {
      const redePublica = redes.find(r => r.nome.toLowerCase().includes('publica') || r.nome.toLowerCase().includes('pública'));
      defaultRedeId = redePublica?.id || redes[0]?.id || '';
      if (defaultRedeId) {
        const matchingEstados = allEstados.filter(e => (e.redeId === defaultRedeId || (e as any).rede?.id === defaultRedeId) && e.ativo !== false);
        if (matchingEstados.length === 1) {
          defaultEstadoId = matchingEstados[0].id;
        }
      }
    }

    setForm({ nome: '', codigoIBGE: '', redeId: defaultRedeId, estadoId: defaultEstadoId, latitude: '', longitude: '' });
    setModalOpen(true);
  };

  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const hasFormData = () => {
    if (editing) return false;
    return !!(form.nome.trim() || form.codigoIBGE.trim() || form.latitude.trim() || form.longitude.trim());
  };

  const handleCancelClick = () => {
    if (hasFormData()) {
      setDiscardConfirmOpen(true);
    } else {
      toast.dismiss();
      setModalOpen(false);
    }
  };

  const handleConfirmDiscard = () => {
    setDiscardConfirmOpen(false);
    toast.dismiss();
    setModalOpen(false);
  };
  const openEdit = (m: Municipio) => {
    setEditing(m);
    setEditTab('dados');
    setRelEscolas([]);
    const estadoId = m.estadoId || m.estado?.id || '';
    const matchingEstado = allEstados.find(e => e.id === estadoId);
    const redeId = m.redeId || m.estado?.rede?.id || matchingEstado?.redeId || '';
    if (redeId) {
      api.get(ROUTES.admin.estadosPorRede(redeId)).then(r => {
        const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
        setEstados(list.filter((x: any) => x.ativo !== false));
      }).catch(() => setEstados([]));
    }
    setForm({
      nome: m.nome,
      codigoIBGE: m.codigoIBGE || '',
      redeId,
      estadoId,
      latitude: m.latitude !== null && m.latitude !== undefined ? String(m.latitude) : '',
      longitude: m.longitude !== null && m.longitude !== undefined ? String(m.longitude) : ''
    });
    setModalOpen(true);
  };

  useEffect(() => {
    if (loc.state?.openNew && !modalOpen && redes.length > 0) {
      openNew();
      navigate(loc.pathname, { replace: true, state: {} });
    }
    if (loc.state?.editId && data && !modalOpen) {
      const found = (data as Municipio[]).find((m: Municipio) => m.id === loc.state.editId);
      if (found) { openEdit(found); navigate(loc.pathname, { replace: true, state: {} }); }
    }
  }, [loc.state, redes, data, modalOpen, navigate]);

  const handleSave = async () => {
    if (!form.nome.trim()) { toast.error('Nome é obrigatório'); return; }
    if (!form.estadoId) { toast.error('Selecione um Estado'); return; }
    if (!form.latitude.trim() || !form.longitude.trim() || isNaN(parseFloat(form.latitude)) || isNaN(parseFloat(form.longitude))) {
      toast.error('Latitude e Longitude válidas são obrigatórias para cadastrar/salvar o município');
      return;
    }
    const latNum = parseFloat(form.latitude);
    const lngNum = parseFloat(form.longitude);
    const normalize = (s: string) =>
      (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    const nomeNorm = normalize(form.nome);

    const calcDistKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
      const R = 6371;
      const dLat = (lat2 - lat1) * (Math.PI / 180);
      const dLon = (lon2 - lon1) * (Math.PI / 180);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * (Math.PI / 180)) *
          Math.cos(lat2 * (Math.PI / 180)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    const municipiosExistentes = (data as Municipio[]) || [];

    // 1. Validar duplicidade de nome no mesmo estado
    const duplicadoNome = municipiosExistentes.find(
      (m) =>
        (!editing || m.id !== editing.id) &&
        (m.estadoId === form.estadoId || m.estado?.id === form.estadoId) &&
        normalize(m.nome) === nomeNorm,
    );
    if (duplicadoNome) {
      toast.error(`Já existe o município "${duplicadoNome.nome}" cadastrado neste estado na mesma rede.`);
      return;
    }

    // 2. Validar proximidade geográfica (mínimo de 5km)
    for (const m of municipiosExistentes) {
      if (
        (!editing || m.id !== editing.id) &&
        (m.estadoId === form.estadoId || m.estado?.id === form.estadoId)
      ) {
        if (
          m.latitude !== null &&
          m.latitude !== undefined &&
          m.longitude !== null &&
          m.longitude !== undefined
        ) {
          const distKm = calcDistKm(
            Number(m.latitude),
            Number(m.longitude),
            latNum,
            lngNum,
          );
          if (distKm < 5.0) {
            toast.error(
              `Já existe o município "${m.nome}" cadastrado nesta mesma região (${distKm.toFixed(2)}km de distância). Não é permitido cadastrar dois municípios no mesmo estado com distância inferior a 5km.`,
            );
            return;
          }
        }
      }
    }

    setSaving(true);
    try {
      const payload = {
        nome: form.nome,
        codigoIBGE: form.codigoIBGE || undefined,
        estadoId: form.estadoId,
        latitude: latNum,
        longitude: lngNum,
      };
      if (editing) { await update.mutateAsync({ id: editing.id, ...payload } as any); toast.success('Município atualizado!'); }
      else { await create.mutateAsync(payload as any); toast.success('Município criado!'); }
      setModalOpen(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const handleGeocodeCityManual = async () => {
    if (!form.nome.trim()) {
      toast.error("Por favor, digite o nome do município primeiro.");
      return;
    }
    const stateId = form.estadoId;
    if (!stateId) {
      toast.error("Por favor, selecione um Estado primeiro.");
      return;
    }
    const uf = allEstados.find(e => e.id === stateId)?.sigla ?? '';
    if (!uf) {
      toast.error("Não foi possível identificar a sigla do Estado.");
      return;
    }

    toast.loading("Buscando localização da cidade...", { id: "geocode" });
    try {
      const q = encodeURIComponent(`${form.nome}, ${uf}, Brasil`);
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=br`,
        { headers: { 'Accept-Language': 'pt-BR' } }
      );
      if (!r.ok) throw new Error("Erro de rede ao buscar.");
      const data = await r.json();
      if (data && data.length > 0) {
        setForm(f => ({
          ...f,
          latitude: String(parseFloat(data[0].lat).toFixed(6)),
          longitude: String(parseFloat(data[0].lon).toFixed(6))
        }));
        toast.success("Localização encontrada com sucesso!", { id: "geocode" });
      } else {
        toast.error("Localização não encontrada para este município. Digite manualmente.", { id: "geocode" });
      }
    } catch (err: any) {
      toast.error(`Falha na busca: ${err.message}`, { id: "geocode" });
    }
  };

  const handleGetDeviceLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocalização não suportada pelo seu navegador.");
      return;
    }
    toast.loading("Obtendo localização do dispositivo...", { id: "gps" });
    navigator.geolocation.getCurrentPosition(
      pos => {
        setForm(f => ({
          ...f,
          latitude: String(pos.coords.latitude.toFixed(6)),
          longitude: String(pos.coords.longitude.toFixed(6))
        }));
        toast.success("Localização do dispositivo obtida!", { id: "gps" });
      },
      err => {
        toast.error(`Falha ao obter localização: ${err.message}`, { id: "gps" });
      },
      { timeout: 8000, enableHighAccuracy: true }
    );
  };

  const handleGetIpLocation = async () => {
    toast.loading("Obtendo localização por IP...", { id: "ip" });
    try {
      const res = await fetch('https://ipapi.co/json/');
      if (res.ok) {
        const data = await res.json();
        setForm(f => ({
          ...f,
          latitude: String(parseFloat(data.latitude).toFixed(6)),
          longitude: String(parseFloat(data.longitude).toFixed(6))
        }));
        toast.success("Localização por IP obtida!", { id: "ip" });
      } else {
        toast.error("Falha ao buscar geolocalização por IP.", { id: "ip" });
      }
    } catch (err: any) {
      toast.error(`Erro ao consultar IP: ${err.message}`, { id: "ip" });
    }
  };

  const handleActivateMunicipio = async (m: Municipio) => {
    try {
      await update.mutateAsync({ id: m.id, ativo: true } as any);
      toast.success('Município ativado com sucesso!');
      refetch();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao ativar município');
    }
  };

  const handleHardDeleteMunicipio = (m: Municipio) => {
    setMunicipioToHardDelete(m);
  };

  const confirmHardDeleteMunicipio = async () => {
    if (!municipioToHardDelete) return;
    const m = municipioToHardDelete;
    setMunicipioToHardDelete(null);
    try {
      await api.delete(`/admin/municipios/hard/${m.id}`);
      toast.success('Município removido com sucesso!');
      refetch();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao remover município permanentemente');
    }
  };

  const requestDelete = (m: Municipio) => {
    const esc = m._count?.escolas || 0;
    if (esc > 0) {
      toast.warning(`Não é possível desativar o município "${m.nome}". Ele possui ${esc} escola${esc !== 1 ? 's' : ''} vinculada${esc !== 1 ? 's' : ''}.`);
      return;
    }
    setDeleteConfirm(m);
  };
  const confirmDeleteMunicipio = async () => {
    if (!deleteConfirm) return;
    const id = deleteConfirm.id;
    setDeleteConfirm(null);
    try {
      await deactivate.mutateAsync(id);
      toast.success('Município desativado com sucesso');
      refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.error?.message || e?.response?.data?.message || 'Não foi possível desativar o município';
      toast.warning(msg);
    }
  };

  const columns: ColumnDef<Municipio>[] = [
    { accessorKey: 'nome', header: 'Nome', cell: ({ row }) => {
      const isAtivo = row.original.ativo;
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          opacity: isAtivo ? 1 : 0.45,
          transition: 'opacity 0.2s ease',
        }}>
          <MapPin size={16} style={{ color: isAtivo ? '#f59e0b' : '#9ca3af', flexShrink: 0 }} />
          <span style={{ fontWeight: 600, color: isAtivo ? 'inherit' : '#64748b' }}>
            {row.original.nome}
          </span>
        </div>
      );
    }},
    { id: 'estado', header: 'Estado', cell: ({ row }) => {
      const isAtivo = row.original.ativo;
      return (
        <span style={{ opacity: isAtivo ? 1 : 0.45 }}>
          {row.original.estado ? `${row.original.estado.nome} (${row.original.estado.sigla})` : '—'}
        </span>
      );
    }},
    { id: 'gestores', header: 'Gestores', cell: ({ row }) => { const v = row.original._count?.usuarios || 0; return <span className={v && row.original.ativo ? 'badge badge-active' : 'badge'} style={v && row.original.ativo ? undefined : { opacity: .35, color: '#9ca3af' }}>{v}</span>; }},
    { accessorKey: 'ativo', header: 'Status', cell: ({ row }) => {
      if (!row.original.ativo) {
        return <span className="badge badge-inactive">Inativo</span>;
      }
      const temEscolas = (row.original._count?.escolas || 0) > 0;
      if (temEscolas) {
        return <span className="badge badge-active" title="Município ativo com escolas vinculadas">Ativo</span>;
      }
      return (
        <span
          className="badge"
          style={{ background: 'rgba(245,158,11,0.15)', color: '#d97706', border: '1px solid rgba(245,158,11,0.35)', fontWeight: 700 }}
          title="Município cadastrado, mas sem escolas vinculadas (Pendente de escolas)"
        >
          Pendente
        </span>
      );
    }},
    { id: 'actions', header: '', cell: ({ row }) => (
      <div style={{ display: 'flex', gap: 4 }}>
        <button className="action-btn" onClick={() => navigate(`/master/municipios/${row.original.id}`)} title="Ver detalhe"><ExternalLink size={15} /></button>
        <button className="action-btn" onClick={() => openEdit(row.original)} title="Editar"><Pencil size={15} /></button>
        {devSettings.allowDeleteMunicipios && (
          row.original.ativo ? (
            <button className="action-btn action-btn-danger" onClick={() => requestDelete(row.original)} title="Desativar"><PowerOff size={15} /></button>
          ) : (
            <>
              <button className="action-btn" onClick={() => handleActivateMunicipio(row.original)}
                title="Reativar" style={{ color: '#10b981', border: '1px solid #10b981', background: 'rgba(16,185,129,0.1)' }}><Power size={15} /></button>
              {devSettings.allowHardDelete && (
                <button className="action-btn action-btn-danger" onClick={() => handleHardDeleteMunicipio(row.original)}
                  title="Remover permanentemente"><Trash2 size={15} /></button>
              )}
            </>
          )
        )}
      </div>
    )},
  ];

  // ── Tabs ───────────────────────────────────────────────────
  const TABS = [
    { key: 'dados' as const, icon: <ClipboardList size={14} />, label: 'Dados' },
    { key: 'escolas' as const, icon: <Building2 size={14} />, label: 'Escolas' },
    { key: 'gestores' as const, icon: <Users size={14} />, label: 'Gestores' },
  ];

  const renderEditTabs = () => editing && (
    <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border, #e5e7eb)', marginBottom: 16 }}>
      {TABS.map(t => (
        <button key={t.key} onClick={() => setEditTab(t.key)} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
          background: 'none', border: 'none', cursor: 'pointer',
          fontWeight: editTab === t.key ? 700 : 500, fontSize: '.83rem',
          color: editTab === t.key ? '#6366f1' : '#6b7280',
          borderBottom: editTab === t.key ? '2px solid #6366f1' : '2px solid transparent',
          marginBottom: -2, transition: 'all .15s',
        }}>{t.icon} {t.label}</button>
      ))}
    </div>
  );

  const renderDadosForm = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label" style={{ fontWeight: 700, color: '#0f172a' }}>
            Rede * {isEstadual && <span style={{ fontSize: '0.68rem', color: '#6366f1', fontWeight: 600 }}>(Fixa)</span>}
          </label>
          <select
            name="redeId"
            id="municipio-redeId"
            data-lab-field="redeId"
            className="form-select"
            value={form.redeId}
            onChange={e => !isEstadual && setForm({ ...form, redeId: e.target.value, estadoId: '' })}
            disabled={isEstadual}
            style={isEstadual ? { background: '#f1f5f9', cursor: 'not-allowed', color: '#334155', fontWeight: 600 } : undefined}
          >
            <option value="">Selecione a rede...</option>
            {redes.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label" style={{ fontWeight: 700, color: '#0f172a' }}>
            Estado * {isEstadual && <span style={{ fontSize: '0.68rem', color: '#6366f1', fontWeight: 600 }}>(Fixo)</span>}
          </label>
          <select
            name="estadoId"
            id="municipio-estadoId"
            data-lab-field="estadoId"
            className="form-select"
            value={form.estadoId}
            onChange={e => !isEstadual && setForm({ ...form, estadoId: e.target.value })}
            disabled={isEstadual || !form.redeId}
            style={isEstadual ? { background: '#f1f5f9', cursor: 'not-allowed', color: '#334155', fontWeight: 600 } : undefined}
          >
            <option value="">{form.redeId ? 'Selecione o estado...' : 'Selecione uma rede primeiro'}</option>
            {(isEstadual && allEstados.length > 0 ? allEstados.filter(e => e.id === userEstadoId) : estados).map(e => (
              <option key={e.id} value={e.id}>{e.nome} ({e.sigla})</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Box de Orientação / Explicação da Rede Selecionada ── */}
      {(() => {
        const selectedRede = redes.find(r => r.id === form.redeId);
        if (!selectedRede) return null;
        return (
          <div style={{
            marginBottom: 12,
            marginTop: -4,
            padding: '8px 12px',
            borderRadius: 8,
            background: 'rgba(99, 102, 241, 0.06)',
            border: '1px solid rgba(99, 102, 241, 0.18)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            fontSize: '.78rem',
            color: '#334155',
            lineHeight: 1.4,
          }}>
            <span style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>🏛️</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#4f46e5', marginBottom: 2 }}>
                Premissa da Rede: {selectedRede.nome}
              </div>
              <div>
                {selectedRede.descricao
                  ? selectedRede.descricao
                  : 'Rede mantenedora institucional vinculada para centralização e gestão de estados, municípios e polos escolares.'}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Alerta: rede selecionada sem estados (somente para Master Admin) */}
      {!isEstadual && form.redeId && !estados.length && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
          marginBottom: 12, marginTop: -4,
        }}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#dc2626" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span style={{ flex: 1, fontSize: '.82rem', color: '#dc2626', fontWeight: 500 }}>
            Nenhum estado cadastrado nesta rede. Cadastre um estado primeiro.
          </span>
          <button type="button" onClick={() => { setModalOpen(false); navigate('/master/estados', { state: { openNew: true } }); }} style={{
            padding: '6px 14px', background: '#dc2626', color: '#fff', border: 'none',
            borderRadius: 8, fontSize: '.78rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
          }}>+ Cadastrar Estado</button>
        </div>
      )}
      
      {/* Mensagem orientativa de estado não selecionado (apenas se realmente não tiver estado) */}
      {!form.estadoId && (
        <div style={{
          fontSize: '.78rem',
          color: '#1e40af',
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 8,
          padding: '9px 14px',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontWeight: 600,
        }}>
          <span style={{ fontSize: '1rem' }}>ℹ️</span>
          <span>Selecione o Estado acima para liberar o preenchimento do município.</span>
        </div>
      )}

      {/* Bloco de Dados e Coordenadas (opaco e bloqueado enquanto o Estado não for selecionado) */}
      <div style={{
        opacity: form.estadoId ? 1 : 0.45,
        pointerEvents: form.estadoId ? 'auto' : 'none',
        transition: 'opacity 0.2s ease',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ fontWeight: 700, color: '#0f172a' }}>Nome *</label>
            <input
              className="form-input"
              name="nome"
              id="municipio-nome"
              data-lab-field="nome"
              value={form.nome}
              onChange={e => setForm({ ...form, nome: e.target.value })}
              placeholder="Digite o nome da cidade"
              disabled={!form.estadoId}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ fontWeight: 700, color: '#0f172a' }}>Código IBGE</label>
            <input
              className="form-input"
              name="codigoIBGE"
              id="municipio-codigoIBGE"
              data-lab-field="codigoIBGE"
              value={form.codigoIBGE}
              onChange={e => setForm({ ...form, codigoIBGE: e.target.value })}
              placeholder="Digite código..."
              disabled={!form.estadoId}
            />
          </div>
        </div>

        {/* Seção Geográfica */}
        <div style={{ marginTop: 12, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label className="form-label" style={{ margin: 0, fontWeight: 700, color: '#0f172a' }}>Geolocalização / Coordenadas *</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={handleGetDeviceLocation}
                disabled={!form.estadoId}
                className="btn-secondary"
                style={{ fontSize: '.75rem', padding: '5px 12px', height: 'auto', display: 'flex', alignItems: 'center', gap: 4, background: '#f1f5f9', border: '1px solid #cbd5e1', cursor: form.estadoId ? 'pointer' : 'not-allowed', borderRadius: 6, color: '#0f172a', fontWeight: 600 }}
              >
                📍 GPS Dispositivo
              </button>
              <button
                type="button"
                onClick={handleGetIpLocation}
                disabled={!form.estadoId}
                className="btn-secondary"
                style={{ fontSize: '.75rem', padding: '5px 12px', height: 'auto', display: 'flex', alignItems: 'center', gap: 4, background: '#f1f5f9', border: '1px solid #cbd5e1', cursor: form.estadoId ? 'pointer' : 'not-allowed', borderRadius: 6, color: '#0f172a', fontWeight: 600 }}
              >
                🌐 Obter por IP
              </button>
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1fr', gap: 12, alignItems: 'end' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: '.75rem', color: '#1e293b', fontWeight: 600 }}>Latitude *</label>
              <input
                className="form-input"
                name="latitude"
                id="municipio-latitude"
                data-lab-field="latitude"
                value={form.latitude}
                onChange={e => setForm({ ...form, latitude: e.target.value })}
                placeholder="---"
                disabled={!form.estadoId}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: '.75rem', color: '#1e293b', fontWeight: 600 }}>Longitude *</label>
              <input
                className="form-input"
                name="longitude"
                id="municipio-longitude"
                data-lab-field="longitude"
                value={form.longitude}
                onChange={e => setForm({ ...form, longitude: e.target.value })}
                placeholder="---"
                disabled={!form.estadoId}
              />
            </div>
            <button
              type="button"
              onClick={handleGeocodeCityManual}
              disabled={!form.estadoId}
              className="btn-primary"
              style={{ height: '36px', padding: '0 10px', fontSize: '.78rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: form.estadoId ? 'pointer' : 'not-allowed', background: 'linear-gradient(135deg,#3b82f6,#7c3aed)', border: 'none', color: '#fff', borderRadius: 8, fontWeight: 600 }}
            >
              🔍 Buscar Cidade
            </button>
          </div>

          {/* Alerta se não houver coordenadas */}
          {form.estadoId && (!form.latitude || !form.longitude) && (
            <div style={{ fontSize: '.78rem', color: '#f43f5e', marginTop: 10, display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
              ⚠️ Sem coordenadas! É obrigatório informar a Latitude e a Longitude para salvar o município.
            </div>
          )}

          {/* Mapa Pequeno */}
          {form.latitude && form.longitude && isFinite(parseFloat(form.latitude)) && isFinite(parseFloat(form.longitude)) && (
            <div style={{ marginTop: 14, height: 160, borderRadius: 10, overflow: 'hidden', border: '1px solid #cbd5e1' }}>
              <MapContainer center={[parseFloat(form.latitude), parseFloat(form.longitude)]} zoom={13} style={{ height: '100%', width: '100%' }} zoomControl={false}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[parseFloat(form.latitude), parseFloat(form.longitude)]} icon={markerPinIcon} />
                <MapCenterController center={[parseFloat(form.latitude), parseFloat(form.longitude)]} />
                <MapClickEvents onClick={(lat, lng) => setForm(f => ({ ...f, latitude: lat.toFixed(6), longitude: lng.toFixed(6) }))} />
              </MapContainer>
            </div>
          )}
        </div>
      </div>
    </>
  );

  const tipoRedeColors: Record<string, string> = { MUNICIPAL: '#10b981', ESTADUAL: '#6366f1', PARTICULAR: '#f59e0b' };

  const renderEscolasList = () => {
    if (relLoading) return <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>Carregando...</div>;
    if (relEscolas.length === 0) return (
      <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>
        <Building2 size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
        <p style={{ margin: 0 }}>Nenhuma escola vinculada a este município</p>
      </div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: '.85rem', color: '#6b7280', marginBottom: 6 }}>{relEscolas.length} escola(s)</div>
        {relEscolas.map(e => {
          const color = tipoRedeColors[e.tipoRede] || '#6b7280';
          return (
            <div key={e.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              borderRadius: 8, border: '1px solid var(--border, #e5e7eb)', background: 'var(--bg-muted, #f9fafb)',
            }}>
              <Building2 size={16} style={{ color: '#3b82f6', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.nome}</div>
                <div style={{ fontSize: '.72rem', color: '#6b7280' }}>{e.codigo} · {e.cidade}</div>
              </div>
              <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '.65rem', fontWeight: 600, background: `${color}18`, color }}>{e.tipoRede}</span>
              <span className="badge badge-active" style={{ fontSize: '.65rem' }}>{e._count?.alunos || 0} alunos</span>
              <span className="badge badge-active" style={{ fontSize: '.65rem' }}>{e._count?.turmas || 0} turmas</span>
            </div>
          );
        })}
      </div>
    );
  };

  const isFormValid = useMemo(() => {
    if (!form.redeId || !form.estadoId || !form.nome.trim()) return false;
    if (!form.latitude.trim() || !form.longitude.trim()) return false;
    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
    return true;
  }, [form]);

  const renderFooter = () => {
    if (editing && editTab !== 'dados') return <button className="btn-cancel" onClick={() => setModalOpen(false)}>Fechar</button>;
    return (
      <>
        <button className="btn-cancel" onClick={editing ? () => setModalOpen(false) : handleCancelClick}>
          Cancelar
        </button>
        <button
          className="btn-save"
          data-lab-action="salvar"
          onClick={handleSave}
          disabled={saving || !isFormValid}
          style={{
            opacity: isFormValid ? 1 : 0.5,
            cursor: isFormValid ? (saving ? 'wait' : 'pointer') : 'not-allowed',
            transition: 'all 0.2s ease',
          }}
          title={!isFormValid ? 'Preencha todos os campos obrigatórios para liberar o salvamento' : undefined}
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </button>
      </>
    );
  };

  const renderEditContent = () => {
    if (!editing) return null;
    switch (editTab) {
      case 'dados': return renderDadosForm();
      case 'escolas': return renderEscolasList();
      case 'gestores': return <UserManagerTab entityType="municipio" entityId={editing.id} label="Gestores Municipais" defaultPerfil="MUNICIPAL" />;
    }
  };

  // ── Agrupamento por Rede ─────────────────────────────────
  const [search, setSearch] = useState('');
  const [collapsedRedes, setCollapsedRedes] = useState<Set<string>>(new Set());

  const toggleRede = (redeId: string) => {
    const next = new Set(collapsedRedes);
    next.has(redeId) ? next.delete(redeId) : next.add(redeId);
    setCollapsedRedes(next);
  };

  const groupedByRede = useMemo(() => {
    let dataset = (data as Municipio[]) || [];
    if (isEstadual && userEstadoId) {
      dataset = dataset.filter((m: Municipio) => m.estadoId === userEstadoId || m.estado?.id === userEstadoId);
    }
    const filtered = search.trim()
      ? dataset.filter((m: Municipio) => m.nome.toLowerCase().includes(search.toLowerCase()))
      : dataset;

    const groups: Record<string, { rede: { id: string; nome: string } | null; items: Municipio[] }> = {};
    filtered.forEach((m: Municipio) => {
      const rede = m.estado?.rede;
      const key = rede?.id || '__sem_rede__';
      if (!groups[key]) groups[key] = { rede: rede || null, items: [] };
      groups[key].items.push(m);
    });

    // Ordena redes por nome, "Sem Rede" no final, e dentro de cada grupo ativos primeiro e inativos no final
    return Object.values(groups)
      .sort((a, b) => {
        if (!a.rede) return 1;
        if (!b.rede) return -1;
        return a.rede.nome.localeCompare(b.rede.nome);
      })
      .map(group => ({
        ...group,
        items: [...group.items].sort((a, b) => {
          if (a.ativo !== b.ativo) return a.ativo ? -1 : 1;
          return a.nome.localeCompare(b.nome);
        }),
      }));
  }, [data, search, isEstadual, userEstadoId]);

  /* ── KPIs e Agregações Únicas ── */
  const stats = useMemo(() => {
    let list = (data as Municipio[]) || [];
    if (isEstadual && userEstadoId) {
      list = list.filter((m: Municipio) => m.estadoId === userEstadoId || m.estado?.id === userEstadoId);
    }

    // 1. Municípios únicos ativos, em operação e inativos
    const munAtivosSet = new Set<string>();
    const munEmOperacaoSet = new Set<string>();
    const munInativosList = list.filter((m: Municipio) => !m.ativo);

    const estadosAtivosSet = new Set<string>();

    list.forEach((m: Municipio) => {
      if (m.ativo) {
        const estadoSigla = (m.estado?.sigla || m.estado?.nome || m.estadoId || '').trim().toUpperCase();
        const nomeNorm = (m.nome || '').trim().toUpperCase();
        const chave = `${nomeNorm}__${estadoSigla}`;
        munAtivosSet.add(chave);

        if ((m._count?.escolas || 0) > 0) {
          munEmOperacaoSet.add(chave);
        }

        if (estadoSigla) estadosAtivosSet.add(estadoSigla);
      }
    });

    const municipiosAtivos = munAtivosSet.size;
    const municipiosEmOperacao = munEmOperacaoSet.size;
    const municipiosPendentes = Math.max(0, municipiosAtivos - municipiosEmOperacao);
    const municipiosInativos = munInativosList.length;

    // 2. Escolas ativas, em atividade, em implantação e inativas
    let escolasAtivas = 0;
    let escolasEmAtividade = 0;
    let escolasEmImplantacao = 0;
    let escolasInativas = 0;
    if (allEscolasGlobal.length > 0) {
      let filteredEscolas = allEscolasGlobal;
      if (isEstadual && userEstadoId) {
        filteredEscolas = allEscolasGlobal.filter((esc: any) => esc.municipio?.estado?.id === userEstadoId || esc.municipio?.estadoId === userEstadoId);
      }
      const ativas = filteredEscolas.filter((esc: any) => esc.ativo);
      escolasAtivas = ativas.length;
      escolasInativas = filteredEscolas.filter((esc: any) => !esc.ativo).length;
      escolasEmAtividade = ativas.filter((e: any) => e.wizardConcluido || (Number(e.wizardEtapaAtual || 0) >= 7 && (e._count?.turmas || 0) > 0 && (e._count?.alunos || 0) > 0)).length;
      escolasEmImplantacao = Math.max(0, escolasAtivas - escolasEmAtividade);
    } else {
      list.filter(m => m.ativo).forEach(m => {
        escolasAtivas += m._count?.escolas || 0;
      });
      escolasEmAtividade = escolasAtivas;
    }

    // 3. Estados únicos ativos e inativos
    let estadosAtivos = isEstadual ? 1 : 0;
    let estadosInativos = 0;
    if (isEstadual) {
      estadosAtivos = 1;
      estadosInativos = 0;
    } else if (allEstadosGlobal.length > 0) {
      const estadosAtivosSiglas = new Set<string>();
      allEstadosGlobal.forEach((e: any) => {
        const siglaNorm = (e.sigla || e.nome || '').trim().toUpperCase();
        if (!siglaNorm) return;
        if (e.ativo) {
          estadosAtivosSiglas.add(siglaNorm);
        }
      });
      estadosAtivos = estadosAtivosSiglas.size;
      estadosInativos = allEstadosGlobal.filter((e: any) => !e.ativo).length;
    } else {
      estadosAtivos = estadosAtivosSet.size || allEstados.length;
    }

    // 4. Redes ativas e inativas
    const redesAtivas = isEstadual ? 1 : (allRedesGlobal.length > 0 ? allRedesGlobal.filter((r: any) => r.ativo).length : redes.length);
    const redesInativas = isEstadual ? 0 : (allRedesGlobal.filter((r: any) => !r.ativo).length);

    return {
      municipiosAtivos,
      municipiosEmOperacao,
      municipiosPendentes,
      municipiosInativos,
      escolasAtivas,
      escolasEmAtividade,
      escolasEmImplantacao,
      escolasInativas,
      estadosAtivos,
      estadosInativos,
      redesAtivas,
      redesInativas,
    };
  }, [data, isEstadual, userEstadoId, allEstados.length, allEstadosGlobal, allEscolasGlobal, allRedesGlobal, redes.length]);

  const kpis = [
    {
      icon: MapPin,
      label: 'Municípios',
      value: stats.municipiosAtivos,
      subValue: (stats.municipiosPendentes > 0 || stats.municipiosInativos > 0)
        ? `${stats.municipiosEmOperacao} em operação • ${stats.municipiosPendentes} pendente${stats.municipiosPendentes !== 1 ? 's' : ''}${stats.municipiosInativos > 0 ? ` • ${stats.municipiosInativos} inativo${stats.municipiosInativos !== 1 ? 's' : ''}` : ''}`
        : `${stats.municipiosAtivos} ativos`,
      color: '#f59e0b'
    },
    {
      icon: Building2,
      label: 'Escolas',
      value: stats.escolasAtivas,
      subValue: (stats.escolasEmImplantacao > 0 || stats.escolasInativas > 0)
        ? `${stats.escolasEmAtividade} em atividade • ${stats.escolasEmImplantacao} em implantação${stats.escolasInativas > 0 ? ` • ${stats.escolasInativas} inativa${stats.escolasInativas !== 1 ? 's' : ''}` : ''}`
        : `${stats.escolasAtivas} ativas`,
      color: '#10b981'
    },
    {
      icon: Globe,
      label: 'Estados',
      value: stats.estadosAtivos,
      subValue: stats.estadosInativos > 0
        ? `${stats.estadosAtivos} ativos • ${stats.estadosInativos} inativo${stats.estadosInativos !== 1 ? 's' : ''}`
        : undefined,
      color: '#6366f1'
    },
    {
      icon: Network,
      label: 'Redes',
      value: stats.redesAtivas,
      subValue: stats.redesInativas > 0
        ? `${stats.redesAtivas} ativas • ${stats.redesInativas} inativa${stats.redesInativas !== 1 ? 's' : ''}`
        : undefined,
      color: '#8b5cf6'
    },
  ];

  return (
    <div className="admin-page animate-fadeIn">

      {/* ═══ HERO BANNER ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #78350f 0%, #b45309 40%, #f59e0b 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(245,158,11,.15)' }} />
        <div style={{ position: 'absolute', bottom: -40, left: '30%', width: 160, height: 160, borderRadius: '50%', background: 'rgba(180,83,9,.1)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <MapPin size={18} style={{ color: '#fde68a' }} />
                <span style={{ color: 'rgba(255,255,255,.4)', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.12em' }}>Gestão</span>
              </div>
              <h1 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>Municípios</h1>
              <p style={{ color: 'rgba(255,255,255,.45)', fontSize: '.82rem', marginTop: 4 }}>
                {stats.municipiosAtivos} município{stats.municipiosAtivos !== 1 ? 's' : ''} ativo{stats.municipiosAtivos !== 1 ? 's' : ''} {stats.municipiosInativos > 0 ? `(${stats.municipiosInativos} inativo${stats.municipiosInativos !== 1 ? 's' : ''})` : ''} {isEstadual ? 'sob gestão estadual' : 'vinculados aos estados'}
              </p>
            </div>
            {canManage && (
              <button
                onClick={openNew}
                data-lab-action="novo-municipio"
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10,
                  background: '#ffffff', color: '#78350f', fontWeight: 700, fontSize: '.85rem', border: 'none', cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.15)', transition: 'all 0.15s ease',
                }}
              >
                <Plus size={16} /> Novo Município
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ═══ KPIS ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
        {kpis.map((k, i) => (
          <div key={i} style={{
            background: 'var(--bg-card, white)', borderRadius: 14, padding: '18px 20px',
            border: '1px solid var(--border, #e5e7eb)', display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10, background: `${k.color}15`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <k.icon size={22} style={{ color: k.color }} />
            </div>
            <div>
              <div style={{ fontSize: '.75rem', color: '#6b7280', fontWeight: 500 }}>{k.label}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-main, #111827)', lineHeight: 1.2 }}>{k.value}</span>
                {k.subValue && (
                  <span style={{ fontSize: '.72rem', color: '#9ca3af', fontWeight: 600 }}>
                    ({k.subValue})
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Carregando...</div>
      ) : groupedByRede.length === 0 ? (
        <div style={{
          background: 'var(--bg-card, white)',
          border: '1px solid var(--border-color, #e5e7eb)',
          borderRadius: 14,
          padding: '60px 20px',
          textAlign: 'center',
          marginTop: 12
        }}>
          <MapPin size={48} style={{ color: 'var(--text-muted, #9ca3af)', marginBottom: 16, opacity: 0.5, margin: '0 auto 12px' }} />
          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-main, #1e293b)', margin: '0 0 8px' }}>
            Nenhum município cadastrado ainda
          </h3>
          <p style={{ fontSize: '.85rem', color: 'var(--text-muted, #6b7280)', maxWidth: 380, margin: '0 auto 20px', lineHeight: 1.5 }}>
            Cadastre o primeiro município para começar a vincular escolas, turnos, turmas e acompanhar o progresso dos alunos.
          </p>
          <button type="button" className="btn-primary" onClick={openNew} style={{ height: 38, padding: '0 20px', fontSize: '.85rem', display: 'inline-flex', alignItems: 'center', gap: 8, borderRadius: 10, margin: '0 auto' }}>
            <Plus size={16} /> Cadastrar Município
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {Object.entries(groupedByRede).map(([redeKey, group]) => {
            const isCollapsed = collapsedRedes.has(redeKey);
            const ativosNoGrupo = group.items.filter(i => i.ativo).length;
            const inativosNoGrupo = group.items.filter(i => !i.ativo).length;
            return (
              <div key={redeKey} style={{ borderRadius: 12, border: '1px solid var(--border, #e5e7eb)', overflow: 'hidden', marginBottom: 32 }}>
                <div style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 16px', background: '#111827', border: 'none',
                  cursor: 'pointer',
                }}>
                  <button onClick={() => toggleRede(redeKey)} style={{
                    display: 'flex', alignItems: 'center', gap: 10, flex: 1,
                    background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0,
                  }}>
                    <Network size={16} style={{ color: '#818cf8', flexShrink: 0 }} />
                    <span style={{ flex: 1, fontWeight: 700, fontSize: '.9rem', color: 'rgba(255,255,255,.9)' }}>
                      {group.rede?.nome || 'Sem Rede'}
                    </span>
                    <span style={{
                      padding: '2px 10px', borderRadius: 20, fontSize: '.72rem', fontWeight: 600,
                      background: 'rgba(129,140,248,.2)', color: '#a5b4fc',
                    }}>
                      {ativosNoGrupo} ativo{ativosNoGrupo !== 1 ? 's' : ''}{inativosNoGrupo > 0 ? ` • ${inativosNoGrupo} inativo` : ''}
                    </span>
                    <ChevronDown size={16} style={{
                      color: '#94a3b8', transition: 'transform .2s',
                      transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    }} />
                  </button>
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Buscar município..."
                    onClick={e => e.stopPropagation()}
                    style={{
                      width: 200, padding: '6px 12px', borderRadius: 8,
                      border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,.08)',
                      color: 'rgba(255,255,255,.8)', fontSize: '.8rem', outline: 'none',
                    }}
                  />
                </div>
                {!isCollapsed && (
                  <div style={{ padding: '0 0 4px' }}>
                    <DataTable columns={columns} data={group.items} isLoading={false} searchPlaceholder="" showToolbar={false} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal open={modalOpen} onClose={handleCancelClick}
        title={editing ? `Editar: ${editing.nome}` : 'Novo Município'}
        footer={renderFooter()}>
        <style>{`.modal-content { max-width: 680px !important; }`}</style>
        {editing ? (<>{renderEditTabs()}{renderEditContent()}</>) : renderDadosForm()}
      </Modal>

      <ConfirmModal
        open={discardConfirmOpen}
        title="Descartar alterações?"
        message="Tem certeza que deseja cancelar? As informações não salvas serão perdidas."
        confirmLabel="Descartar"
        onCancel={() => setDiscardConfirmOpen(false)}
        onConfirm={handleConfirmDiscard}
      />

      <ConfirmModal
        open={!!deleteConfirm}
        title="Desativar município"
        message={deleteConfirm ? `Desativar o município "${deleteConfirm.nome}"?` : ''}
        confirmLabel="Desativar"
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => void confirmDeleteMunicipio()}
      />

      <PasswordConfirmModal
        open={!!municipioToHardDelete}
        title="Remover Município Permanentemente"
        description={`Para remover permanentemente o município "${municipioToHardDelete?.nome}", digite a palavra-chave de autorização:`}
        requiredPassword="marcelo"
        confirmLabel="Remover Município"
        onCancel={() => setMunicipioToHardDelete(null)}
        onConfirm={confirmHardDeleteMunicipio}
      />
    </div>
  );
}
